import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Trash, MapPin, Check } from "@phosphor-icons/react";

export default function ShopPincodes() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({
    pincode: "", area: "", delivery_mode: "both",
    eta_minutes: 30, delivery_charge: 25, min_order_value: 99
  });

  const load = async () => setList((await api.get("/pincodes")).data);
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.pincode || !form.area) return toast.error("Pincode and area required");
    try {
      await api.post("/pincodes", {
        ...form,
        eta_minutes: parseInt(form.eta_minutes),
        delivery_charge: parseFloat(form.delivery_charge),
        min_order_value: parseFloat(form.min_order_value),
        is_active: true,
      });
      toast.success("Pincode added");
      setForm({ pincode: "", area: "", delivery_mode: "both", eta_minutes: 30, delivery_charge: 25, min_order_value: 99 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this pincode?")) return;
    await api.delete(`/pincodes/${id}`);
    load();
  };

  const updateField = async (p, field, value) => {
    const payload = {
      pincode: p.pincode,
      area: p.area,
      delivery_mode: p.delivery_mode,
      eta_minutes: p.eta_minutes,
      delivery_charge: p.delivery_charge,
      min_order_value: p.min_order_value,
      is_active: p.is_active,
      [field]: value,
    };
    try {
      const r = await api.patch(`/pincodes/${p.id}`, payload);
      setList((prev) => prev.map((x) => x.id === p.id ? r.data : x));
      toast.success("Updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Serviceable Pincodes</h1>
        <p className="text-gray-600 text-sm">Add the pincodes you deliver to. Set ETA, delivery charge and minimum order per area.</p>
      </div>

      {/* Add form — stacked on mobile, 6-col on desktop */}
      <form onSubmit={add} className="qd-card p-4 grid grid-cols-2 sm:grid-cols-6 gap-3">
        <input className="qd-input col-span-1 sm:col-span-1" placeholder="Pincode" value={form.pincode}
          onChange={(e) => setForm({ ...form, pincode: e.target.value })} data-testid="pin-pincode" />
        <input className="qd-input col-span-1 sm:col-span-2" placeholder="Area name" value={form.area}
          onChange={(e) => setForm({ ...form, area: e.target.value })} data-testid="pin-area" />
        <select className="qd-input col-span-1 sm:col-span-1" value={form.delivery_mode}
          onChange={(e) => setForm({ ...form, delivery_mode: e.target.value })} data-testid="pin-mode">
          <option value="both">Both</option>
          <option value="self">Self only</option>
          <option value="third_party">3rd-party only</option>
        </select>
        <input className="qd-input col-span-1 sm:col-span-1" type="number" placeholder="ETA min" value={form.eta_minutes}
          onChange={(e) => setForm({ ...form, eta_minutes: e.target.value })} data-testid="pin-eta" />
        <button className="qd-btn-primary col-span-2 sm:col-span-1" data-testid="pin-add-btn">Add</button>
      </form>

      {list.length === 0 && (
        <div className="qd-card p-6 text-center text-sm text-gray-500" data-testid="pin-empty-state">
          No pincodes added yet. Add at least one pincode above so customers can place orders.
        </div>
      )}

      {/* Mobile: stacked card list */}
      <div className="space-y-3 sm:hidden">
        {list.map((p) => (
          <div key={p.id} className="qd-card p-4 space-y-3" data-testid={`pin-card-${p.pincode}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-heading font-black text-lg leading-none">{p.pincode}</div>
                <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <MapPin size={12} className="text-gray-400" /> {p.area}
                </div>
              </div>
              <button onClick={() => del(p.id)} className="text-red-500 p-1 -m-1" data-testid={`pin-del-${p.pincode}`}>
                <Trash size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">Delivery mode</div>
                <select value={p.delivery_mode}
                  onChange={(e) => updateField(p, "delivery_mode", e.target.value)}
                  className="w-full bg-transparent text-sm font-semibold capitalize border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#F8CB46]"
                  data-testid={`pin-mode-${p.pincode}`}>
                  <option value="both">Both</option>
                  <option value="self">Self only</option>
                  <option value="third_party">3rd party only</option>
                </select>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">ETA</div>
                <EditCell value={p.eta_minutes} suffix="min" min={1} max={240}
                  onSave={(v) => updateField(p, "eta_minutes", parseInt(v))}
                  testId={`pin-eta-${p.pincode}`} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">Delivery charge</div>
                <EditCell value={p.delivery_charge} prefix="₹" min={0} step={1}
                  onSave={(v) => updateField(p, "delivery_charge", parseFloat(v))}
                  testId={`pin-charge-${p.pincode}`} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">Min order</div>
                <EditCell value={p.min_order_value} prefix="₹" min={0} step={1}
                  onSave={(v) => updateField(p, "min_order_value", parseFloat(v))}
                  testId={`pin-minorder-${p.pincode}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop/tablet: table */}
      <div className="qd-card overflow-x-auto hidden sm:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-100">
              <th className="p-3 font-semibold">Pincode</th>
              <th className="p-3 font-semibold">Area</th>
              <th className="p-3 font-semibold">Mode</th>
              <th className="p-3 font-semibold">ETA</th>
              <th className="p-3 font-semibold">Charge</th>
              <th className="p-3 font-semibold">Min order</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-b border-gray-50" data-testid={`pin-row-${p.pincode}`}>
                <td className="p-3 font-mono">{p.pincode}</td>
                <td className="p-3"><MapPin size={14} className="inline text-gray-400" /> {p.area}</td>
                <td className="p-3">
                  <select value={p.delivery_mode}
                    onChange={(e) => updateField(p, "delivery_mode", e.target.value)}
                    className="bg-transparent text-sm font-semibold capitalize border border-gray-200 rounded-lg px-2 py-1 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#F8CB46]"
                    data-testid={`pin-mode-desktop-${p.pincode}`}>
                    <option value="both">Both</option>
                    <option value="self">Self only</option>
                    <option value="third_party">3rd party only</option>
                  </select>
                </td>
                <td className="p-3">
                  <EditCell value={p.eta_minutes} suffix="min" min={1} max={240}
                    onSave={(v) => updateField(p, "eta_minutes", parseInt(v))}
                    testId={`pin-eta-desktop-${p.pincode}`} />
                </td>
                <td className="p-3">
                  <EditCell value={p.delivery_charge} prefix="₹" min={0} step={1}
                    onSave={(v) => updateField(p, "delivery_charge", parseFloat(v))}
                    testId={`pin-charge-desktop-${p.pincode}`} />
                </td>
                <td className="p-3">
                  <EditCell value={p.min_order_value} prefix="₹" min={0} step={1}
                    onSave={(v) => updateField(p, "min_order_value", parseFloat(v))}
                    testId={`pin-minorder-desktop-${p.pincode}`} />
                </td>
                <td className="p-3"><button onClick={() => del(p.id)} className="text-red-500" data-testid={`pin-del-desktop-${p.pincode}`}><Trash size={16} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditCell({ value, onSave, prefix = "", suffix = "", min = 0, max, step = 1, testId }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);

  useEffect(() => { setV(value); }, [value]);

  const commit = async () => {
    if (String(v) === String(value)) { setEditing(false); return; }
    await onSave(v);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="inline-flex items-center gap-1">
        {prefix && <span className="text-gray-500">{prefix}</span>}
        <input type="number" autoFocus
          value={v} min={min} max={max} step={step}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") { setV(value); setEditing(false); }
          }}
          className="w-16 px-2 py-1 border border-[#F8CB46] rounded-lg font-semibold focus:outline-none focus:ring-2 focus:ring-[#F8CB46]/30"
          data-testid={testId} />
        {suffix && <span className="text-gray-500 text-xs">{suffix}</span>}
        <button onClick={commit} className="w-6 h-6 rounded-full bg-[#15803D] text-white flex items-center justify-center" data-testid={`${testId}-save`}>
          <Check size={12} weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <button onClick={() => setEditing(true)}
      className="inline-flex items-center gap-1 font-bold px-2 py-1 rounded-lg hover:bg-[#F8CB46]/15 transition border border-transparent hover:border-[#F8CB46]/40"
      data-testid={testId}>
      {prefix}{value}{suffix && ` ${suffix}`}
    </button>
  );
}
